import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import emailjs from "@emailjs/browser";
import Seo from "../components/Seo";
import cvData from "../data/cvData";
import { easing } from "../utils/motionVariants";
import "./NotFoundPage.css";

export default function NotFoundPage() {
  const { name, contacts } = cvData;
  const formRef = useRef(null);
  const [formStatus, setFormStatus] = useState({ state: "idle", message: "" });

  const emailJsServiceId = process.env.REACT_APP_EMAILJS_SERVICE_ID || "YOUR_SERVICE_ID";
  const emailJsTemplateId = process.env.REACT_APP_EMAILJS_TEMPLATE_ID || "YOUR_TEMPLATE_ID";
  const emailJsPublicKey = "SqnD7Z2Ub11QXHwaR";
  const isEmailConfigured =
    emailJsServiceId !== "YOUR_SERVICE_ID" && emailJsTemplateId !== "YOUR_TEMPLATE_ID";

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!isEmailConfigured) {
      setFormStatus({ state: "error", message: "Email service is not configured yet." });
      return;
    }

    setFormStatus({ state: "sending", message: "Sending..." });
    emailjs
      .sendForm(emailJsServiceId, emailJsTemplateId, formRef.current, {
        publicKey: emailJsPublicKey,
      })
      .then(() => {
        setFormStatus({ state: "success", message: "Message sent! I'll reply soon." });
        formRef.current?.reset();
      })
      .catch(() => {
        setFormStatus({
          state: "error",
          message: "Could not send right now. Please try again or use another channel.",
        });
      });
  };

  return (
    <motion.section
      className="section notfound-section"
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease: easing }}
    >
      <Seo
        title="404 - Page not found"
        description="The page you're looking for couldn't be found. Head back to Hirun Kolongahapitiya's portfolio home."
        noindex
      />

      <div className="notfound-layout">
        <motion.div
          className="notfound-intro"
          initial={{ scale: 0.92, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.45, type: "spring", stiffness: 180 }}
        >
          <div className="notfound-avatar">
            <img
              src="/assets/profile.jpg"
              alt={`${name || "Hirun Kolongahapitiya"} portrait`}
              loading="lazy"
            />
          </div>
          <p className="eyebrow">404 - Page not found</p>
          <h2>Looks like that page wandered off.</h2>
          <p className="notfound-lead">
            No worries - head back home, or drop me a quick note below and I'll get back to you.
          </p>
          <div className="notfound-actions">
            <Link to="/" className="notfound-btn primary">Go to Home</Link>
            <a className="notfound-btn ghost" href={`mailto:${contacts.email}`}>
              Email me directly
            </a>
          </div>
        </motion.div>

        <motion.div
          className="notfound-form-card"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.5, ease: easing }}
        >
          <div className="notfound-form-head">
            <p className="eyebrow">Send a Message</p>
            <h3>Tell me what you were looking for</h3>
          </div>
          <form ref={formRef} className="notfound-form" onSubmit={handleSubmit} noValidate>
            <div className="notfound-form-row">
              <label className="notfound-form-field">
                <span>Full Name</span>
                <input type="text" name="from_name" placeholder="Jane Doe" required />
              </label>
              <label className="notfound-form-field">
                <span>Email Address</span>
                <input type="email" name="reply_to" placeholder="jane@company.com" required />
              </label>
            </div>
            <label className="notfound-form-field">
              <span>Subject</span>
              <input type="text" name="subject" placeholder="What would you like to discuss?" required />
            </label>
            <label className="notfound-form-field">
              <span>Message</span>
              <textarea
                name="message"
                rows="4"
                placeholder="A few words about your goals, timeline, and how I can help."
                required
              />
            </label>
            <input type="hidden" name="title" value="Portfolio 404 Contact" />
            <div className="notfound-form-actions">
              <button
                type="submit"
                className="notfound-form-submit"
                disabled={formStatus.state === "sending"}
              >
                {formStatus.state === "sending" ? "Sending..." : "Send Message"}
              </button>
              {formStatus.message && (
                <p
                  className={`notfound-form-status ${
                    formStatus.state === "success"
                      ? "success"
                      : formStatus.state === "error"
                      ? "error"
                      : ""
                  }`}
                  role={formStatus.state === "error" ? "alert" : undefined}
                >
                  {formStatus.message}
                </p>
              )}
            </div>
          </form>
        </motion.div>
      </div>
    </motion.section>
  );
}
